import { motion } from 'framer-motion';
import { Database, FileJson, Server, Globe, HardDrive } from 'lucide-react';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

interface ConnectorTileProps {
    id: string;
    label: string;
    icon?: React.ElementType;
    selected: boolean;
    onClick: () => void;
}

export const ConnectorTile = ({ id, label, icon: Icon, selected, onClick }: ConnectorTileProps) => {
    return (
        <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClick}
            className={cn(
                "relative flex flex-col items-center justify-center p-6 cursor-pointer rounded-2xl border transition-all duration-300",
                "bg-white dark:bg-white/5 backdrop-blur-md",
                selected
                    ? "border-[#002F5D] shadow-lg shadow-blue-900/10 bg-[#002F5D]/5 dark:bg-[#002F5D]/20"
                    : "border-slate-200 dark:border-white/10 hover:border-[#002F5D]/50 dark:hover:border-white/30 hover:shadow-lg dark:hover:shadow-purple-500/10"
            )}
        >
            {/* Glossy overlay (Dark only) */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 to-transparent opacity-0 dark:hover:opacity-100 transition-opacity pointer-events-none hidden dark:block" />

            <div className={cn(
                "mb-3 p-3 rounded-xl transition-colors",
                selected ? "bg-[#002F5D] text-white" : "bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-gray-300 group-hover:text-[#002F5D] dark:group-hover:text-white"
            )}>
                {Icon ? <Icon size={32} strokeWidth={1.5} /> : <Database size={32} />}
            </div>

            <span className={cn(
                "font-medium text-sm text-center",
                selected ? "text-[#002F5D] dark:text-blue-200" : "text-slate-600 dark:text-gray-400 group-hover:text-slate-900 dark:group-hover:text-gray-200"
            )}>
                {label}
            </span>

            {selected && (
                <motion.div
                    layoutId="outline"
                    className="absolute inset-0 border-2 border-[#002F5D] rounded-2xl"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
            )}
        </motion.div>
    );
};
